import React from 'react';
import { AlertTriangle } from 'lucide-react';

// Componente Modal para mostrar errores
interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  type?: 'simple' | 'similar' | 'exacto' | 'exactoActivo';
  ingredientesSimilares?: any[];
  ingredienteExacto?: any | null;
  onReactivarIngrediente?: (ingrediente: any) => void;
  onContinuarDeTodasFormas?: () => void;
  onSeleccionarSimilar?: (ingrediente: any) => void;
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  message,
  type = 'simple',
  ingredientesSimilares = [],
  ingredienteExacto = null,
  onReactivarIngrediente,
  onContinuarDeTodasFormas,
  onSeleccionarSimilar,
}) => {
  if (!isOpen) return null;

  const esSimilar = type === 'similar' && ingredientesSimilares.length > 0;
  const esExacto = type === 'exacto' && ingredienteExacto;
  const esExactoActivo = type === 'exactoActivo' && ingredienteExacto;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {esSimilar
                ? 'Ingredientes Similares Encontrados'
                : esExacto
                  ? 'Ingrediente Existente (Inactivo)'
                  : esExactoActivo
                    ? 'Ingrediente Ya Existe'
                    : 'Error'}
            </h3>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-4">
            {message}
          </p>

          {esSimilar && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Los siguientes ingredientes son similares al que intentas crear:
              </p>

              <div className="space-y-3">
                {ingredientesSimilares.map((ingrediente) => (
                  <div
                    key={ingrediente.id}
                    className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {ingrediente.nombre}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Categoría: {ingrediente.categoria}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Stock: {ingrediente.stock_actual}{' '}
                          {ingrediente.unidad_medida?.abreviacion ||
                            ingrediente.unidad_medida}
                        </p>
                        <p
                          className={`text-sm font-medium ${
                            ingrediente.activo
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {ingrediente.activo ? 'Activo' : 'Inactivo'}
                        </p>
                      </div>

                      <div className="ml-4 space-x-2">
                        {ingrediente.activo && onSeleccionarSimilar && (
                          <button
                            onClick={() => onSeleccionarSimilar(ingrediente)}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors duration-200"
                          >
                            Seleccionar
                          </button>
                        )}
                        {!ingrediente.activo && onReactivarIngrediente && (
                          <button
                            onClick={() => onReactivarIngrediente(ingrediente)}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors duration-200"
                          >
                            Reactivar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Sugerencia:</strong> Puedes seleccionar un ingrediente
                  existente, reactivar uno inactivo o continuar creando uno
                  nuevo si realmente necesitas un ingrediente diferente.
                </p>
              </div>
            </div>
          )}

          {esExacto && ingredienteExacto && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Ya existe un ingrediente con el mismo nombre:
              </p>

              <div className="border border-orange-200 dark:border-orange-600 rounded-lg p-4 bg-orange-50 dark:bg-orange-900/20">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {ingredienteExacto.nombre}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Categoría: {ingredienteExacto.categoria}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Stock: {ingredienteExacto.stock_actual}{' '}
                      {ingredienteExacto.unidad_medida?.abreviacion ||
                        ingredienteExacto.unidad_medida}
                    </p>
                    <p className="text-sm font-medium text-red-600 dark:text-red-400">
                      Estado: Inactivo
                    </p>
                  </div>

                  {onReactivarIngrediente && (
                    <button
                      onClick={() => onReactivarIngrediente(ingredienteExacto)}
                      className="ml-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors duration-200"
                    >
                      Reactivar
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  <strong>Recomendación:</strong> Te sugerimos reactivar el
                  ingrediente existente en lugar de crear uno nuevo para
                  mantener el historial y evitar duplicados.
                </p>
              </div>
            </div>
          )}

          {esExactoActivo && ingredienteExacto && (
            <div className="space-y-4">
              <div className="border border-red-200 dark:border-red-600 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {ingredienteExacto.nombre}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Categoría: {ingredienteExacto.categoria}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Stock: {ingredienteExacto.stock_actual}{' '}
                      {ingredienteExacto.unidad_medida?.abreviacion ||
                        ingredienteExacto.unidad_medida}
                    </p>
                    <p className="text-sm font-medium text-green-600 dark:text-green-400">
                      Estado: Activo
                    </p>
                  </div>

                  {onSeleccionarSimilar && (
                    <button
                      onClick={() => onSeleccionarSimilar(ingredienteExacto)}
                      className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors duration-200"
                    >
                      Seleccionar
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-300">
                  <strong>Sugerencia:</strong> Ya existe un ingrediente activo
                  con este nombre. Puedes seleccionarlo para agregarlo a tu
                  orden.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          {esSimilar ? (
            <>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Volver
              </button>
              {onContinuarDeTodasFormas && (
                <button
                  onClick={onContinuarDeTodasFormas}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Continuar de Todas Formas
                </button>
              )}
            </>
          ) : esExacto ? (
            <>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Volver
              </button>
              {onContinuarDeTodasFormas && (
                <button
                  onClick={onContinuarDeTodasFormas}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Crear de Todas Formas
                </button>
              )}
            </>
          ) : esExactoActivo ? (
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Volver
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Aceptar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorModal;
